import { ArrowRight, Sparkles, Code, Database, FileJson, Zap, Box, ChevronDown, Download, CheckCircle2, BookOpen, Wrench, Key, Lock, Clock, Link, Hash, Shield, Users, FileCode, Settings, GitCompare, Menu, X } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

interface LandingPageProps {
  onGetStarted: () => void;
  onOpenAPIDocs: () => void;
  onOpenDashboard: () => void;
  onOpenDevTools: () => void;
}

function LandingPage({ onGetStarted, onOpenAPIDocs, onOpenDashboard, onOpenDevTools }: LandingPageProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFBFC] overflow-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-200 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF6B6B] rounded-lg flex items-center justify-center rotate-3">
              <Box className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-gray-900">JsonToonCraft</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {/* Direct Nav Items */}
            <button
              onClick={() => document.getElementById('mock-api')?.scrollIntoView({ behavior: 'smooth' })}
              className="hidden lg:flex items-center gap-1.5 px-3 py-2 text-[#1A535C] hover:bg-gray-100 rounded-lg text-sm font-medium transition-all"
            >
              <Database className="w-4 h-4" />
              Mock APIs
            </button>
            <button
              onClick={onOpenAPIDocs}
              className="flex items-center gap-1.5 px-3 py-2 text-[#1A535C] hover:bg-gray-100 rounded-lg text-sm font-medium transition-all"
            >
              <FileJson className="w-4 h-4" />
              Docs
            </button>
            <button
              onClick={() => document.getElementById('developer-tools')?.scrollIntoView({ behavior: 'smooth' })}
              className="hidden lg:flex items-center gap-1.5 px-3 py-2 text-[#1A535C] hover:bg-gray-100 rounded-lg text-sm font-medium transition-all"
            >
              <Wrench className="w-4 h-4" />
              Dev Tools
            </button>

            {/* Resources Dropdown (Blog + VS Code) */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1 px-3 py-2 text-[#1A535C] hover:bg-gray-100 rounded-lg text-sm font-medium transition-all"
              >
                More
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-2 animate-fade-in-up">
                  <a
                    href="/blog"
                    className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <BookOpen className="w-4 h-4 text-[#4ECDC4]" />
                    <div>
                      <div className="font-medium">Blog</div>
                      <div className="text-xs text-gray-500">Guides & tutorials</div>
                    </div>
                  </a>
                  <a
                    href="https://marketplace.visualstudio.com/items?itemName=krishcdbry.jsontooncraft"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Download className="w-4 h-4 text-blue-500" />
                    <div>
                      <div className="font-medium">VS Code Extension</div>
                      <div className="text-xs text-gray-500">Convert in editor</div>
                    </div>
                  </a>
                </div>
              )}
            </div>

            {/* Primary Actions */}
            <button
              onClick={onOpenDashboard}
              className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all"
            >
              Dashboard
            </button>
            <button
              onClick={onGetStarted}
              className="px-4 py-2 bg-[#FF6B6B] text-white rounded-lg text-sm font-medium hover:bg-[#ff5252] transition-all hover:scale-105"
            >
              Playground
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={onGetStarted}
              className="px-4 py-2 bg-[#FF6B6B] text-white rounded-lg text-sm font-medium hover:bg-[#ff5252] transition-all"
            >
              Playground
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 py-4 px-6 space-y-1 animate-fade-in-up">
            <a
              href="/blog"
              className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <BookOpen className="w-5 h-5 text-[#4ECDC4]" />
              <span className="font-medium">Blog</span>
            </a>
            <button
              onClick={() => { onOpenAPIDocs(); setIsMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <FileJson className="w-5 h-5 text-[#FF6B6B]" />
              <span className="font-medium">API Docs</span>
            </button>
            <button
              onClick={() => {
                document.getElementById('developer-tools')?.scrollIntoView({ behavior: 'smooth' });
                setIsMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <Wrench className="w-5 h-5 text-[#1A535C]" />
              <span className="font-medium">Dev Tools</span>
            </button>
            <button
              onClick={() => {
                document.getElementById('mock-api')?.scrollIntoView({ behavior: 'smooth' });
                setIsMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <Database className="w-5 h-5 text-purple-500" />
              <span className="font-medium">Mock APIs</span>
            </button>
            <a
              href="https://marketplace.visualstudio.com/items?itemName=krishcdbry.jsontooncraft"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <Download className="w-5 h-5 text-blue-500" />
              <span className="font-medium">VS Code Extension</span>
            </a>
            <div className="border-t border-gray-100 my-2 pt-2">
              <button
                onClick={() => { onOpenDashboard(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] text-white rounded-lg font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span>Dashboard</span>
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section - Asymmetric */}
      <section className="relative pt-32 pb-20 px-6">
        {/* Animated Grid Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgb(203 213 225 / 0.3) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#FAFBFC] via-transparent to-[#FAFBFC]"></div>
        </div>

        {/* Floating animated elements */}
        <div className="absolute top-20 left-10 w-16 h-16 bg-[#FF6B6B]/10 rounded-2xl rotate-12 animate-float"></div>
        <div className="absolute top-40 right-20 w-20 h-20 bg-[#4ECDC4]/10 rounded-full animate-float-delayed"></div>
        <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-[#1A535C]/10 rounded-lg rotate-45 animate-float-slow"></div>
        <div className="absolute top-1/2 right-10 w-8 h-8 bg-[#FF6B6B]/20 rounded-full animate-pulse"></div>

        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FFE5D9] rounded-full text-sm text-[#FF6B6B] font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Developer Toolkit
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Your complete
                <br />
                <span className="bg-gradient-to-r from-[#FF6B6B] via-[#1A535C] to-[#4ECDC4] bg-clip-text text-transparent">
                  developer toolkit.
                </span>
              </h1>

              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                Convert JSON to any format. Generate mock REST APIs. Access 10+ developer tools.
                Everything you need to build and ship faster — all in one place.
              </p>

              {/* Product Hunt Badge */}
              <div className="mb-8">
                <a
                  href="https://www.producthunt.com/products/jsontooncraft?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-jsontooncraft"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block hover:opacity-90 transition-opacity"
                >
                  <img
                    src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1042415&theme=light&t=1764235789946"
                    alt="Jsontooncraft - Your complete developer toolkit. | Product Hunt"
                    style={{ width: '250px', height: '54px' }}
                    width="250"
                    height="54"
                  />
                </a>
              </div>

              <div className="flex flex-wrap gap-4">
                <button
                  onClick={onGetStarted}
                  className="group px-8 py-4 bg-[#1A535C] text-white rounded-xl font-medium flex items-center gap-2 hover:bg-[#14424a] transition-all hover:scale-105 shadow-lg shadow-[#1A535C]/20"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={onGetStarted}
                  className="px-8 py-4 bg-white text-gray-900 rounded-xl font-medium border border-gray-200 hover:border-gray-300 transition-all hover:scale-105"
                >
                  View Examples
                </button>
              </div>

              {/* Mini stats */}
              <div className="flex gap-8 mt-12">
                <div>
                  <div className="text-2xl font-bold text-gray-900">15+</div>
                  <div className="text-sm text-gray-500">Dev Tools</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">8</div>
                  <div className="text-sm text-gray-500">Mock Templates</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">5</div>
                  <div className="text-sm text-gray-500">API Formats</div>
                </div>
              </div>
            </div>

            {/* Right - Visual Element */}
            <div className="relative">
              <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 p-8 animate-fade-in-up">
                {/* Mock code editor */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-[#FF6B6B] animate-pulse"></div>
                    <div className="w-3 h-3 rounded-full bg-[#4ECDC4]"></div>
                    <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                  </div>

                  <div className="font-mono text-sm space-y-2">
                    <div className="text-gray-400">{"{"}</div>
                    <div className="pl-4 text-[#4ECDC4]">"name"<span className="text-gray-600">:</span> <span className="text-[#FF6B6B]">"JsonToonCraft"</span>,</div>
                    <div className="pl-4 text-[#4ECDC4]">"type"<span className="text-gray-600">:</span> <span className="text-[#FF6B6B]">"toolkit"</span></div>
                    <div className="text-gray-400">{"}"}</div>
                  </div>

                  <div className="pt-4 mt-4 border-t border-gray-100">
                    <div className="text-xs text-gray-400 mb-2">→ TypeScript</div>
                    <div className="font-mono text-sm text-gray-600">
                      interface <span className="text-[#1A535C] font-semibold">Root</span> {"{"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced floating accent elements with animations */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#4ECDC4] rounded-2xl rotate-12 opacity-20 animate-float"></div>
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-[#FF6B6B] rounded-full opacity-20 animate-float-delayed"></div>
              <div className="absolute top-1/2 -right-8 w-10 h-10 bg-[#1A535C]/10 rounded-lg rotate-45 animate-float-slow"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features - Bento Grid */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Built for modern development</h2>
            <p className="text-gray-600">Conversions, mock APIs, and essential dev tools in one place</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Large card */}
            <div className="md:col-span-2 bg-gradient-to-br from-[#1A535C] to-[#14424a] rounded-2xl p-8 text-white">
              <Code className="w-10 h-10 mb-4 opacity-90" />
              <h3 className="text-2xl font-bold mb-3">TypeScript Generator</h3>
              <p className="text-white/80 mb-6">
                Automatically generate type-safe TypeScript interfaces from any JSON structure.
                Nested objects, arrays, everything handled.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm">Interfaces</span>
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm">Types</span>
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm">Nested</span>
              </div>
            </div>

            {/* Small card */}
            <div className="bg-[#FFE5D9] rounded-2xl p-8">
              <Database className="w-10 h-10 mb-4 text-[#FF6B6B]" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Zod Schemas</h3>
              <p className="text-gray-700 text-sm">
                Runtime validation schemas generated instantly
              </p>
            </div>

            {/* Small card */}
            <div className="bg-[#E8F8F6] rounded-2xl p-8">
              <FileJson className="w-10 h-10 mb-4 text-[#4ECDC4]" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">OpenAPI</h3>
              <p className="text-gray-700 text-sm">
                OpenAPI 3.1 compatible schemas for your APIs
              </p>
            </div>

            {/* Large card */}
            <div className="md:col-span-2 bg-gradient-to-br from-[#4ECDC4] to-[#3db8af] rounded-2xl p-8 text-white">
              <Zap className="w-10 h-10 mb-4" />
              <h3 className="text-2xl font-bold mb-3">TOON Format</h3>
              <p className="text-white/90 mb-6">
                Reduce LLM token usage by 30-60% with compact TOON format.
                Perfect for AI applications and prompt optimization.
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                Up to 60% token savings
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mock API Generator Section */}
      <section id="mock-api" className="py-20 px-6 bg-gradient-to-br from-[#1A535C] via-[#14424a] to-[#1A535C] text-white overflow-hidden relative">
        {/* Animated background elements */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-[#4ECDC4]/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FF6B6B]/10 rounded-full blur-3xl animate-float-delayed"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#4ECDC4]/20 backdrop-blur-sm rounded-full text-sm text-[#4ECDC4] font-medium mb-6 border border-[#4ECDC4]/30">
              <Sparkles className="w-4 h-4" />
              New Feature
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Mock API Generator
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
              Create instant mock REST APIs for prototyping and testing. Choose from pre-built templates or create custom endpoints with your own JSON data.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
            {/* Left - Feature highlights */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#4ECDC4] rounded-xl flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Instant Deployment</h3>
                  <p className="text-white/70">
                    Create your mock API in seconds. No server setup, no configuration. Just pick a template or paste your JSON and get a live URL instantly.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#FF6B6B] rounded-xl flex items-center justify-center">
                  <Database className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">8 Pre-built Templates</h3>
                  <p className="text-white/70">
                    Users, products, movies, todos, posts, countries, comments, and complex nested data. Perfect starting points for any project.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Advanced Features</h3>
                  <p className="text-white/70">
                    Filtering, sorting, pagination, custom HTTP methods, response delays, status codes. Everything you need for realistic testing.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Rate Limited & Secure</h3>
                  <p className="text-white/70">
                    10 req/hour without API key, 500 req/hour with API key. HTTPS, CORS enabled, perfect for frontend development.
                  </p>
                </div>
              </div>
            </div>

            {/* Right - Code example */}
            <div className="relative">
              <div className="bg-gray-900 rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
                {/* Terminal header */}
                <div className="bg-gray-800 px-4 py-3 flex items-center gap-2 border-b border-gray-700">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="ml-2 text-xs text-gray-400 font-mono">Terminal</span>
                </div>

                {/* Code content */}
                <div className="p-6 font-mono text-sm space-y-4">
                  <div>
                    <div className="text-gray-500 mb-2"># Create your mock API</div>
                    <div className="text-green-400">$ curl https://jsontooncraft.com/api/mock/abc123/movies</div>
                  </div>

                  <div>
                    <div className="text-gray-500 mb-2"># Filter and paginate</div>
                    <div className="text-green-400">$ curl &quot;...?genre=Action&_limit=10&_page=1&quot;</div>
                  </div>

                  <div>
                    <div className="text-gray-500 mb-2"># With API key for higher limits</div>
                    <div className="text-green-400">$ curl -H &quot;X-API-Key: your-key&quot; ...</div>
                  </div>

                  <div className="text-blue-400 mt-4">
                    → 200 OK (12ms)
                  </div>
                </div>
              </div>

              {/* Floating stats */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-xl">
                <div className="text-2xl font-bold text-gray-900">10+</div>
                <div className="text-xs text-gray-600">Endpoints</div>
              </div>

              <div className="absolute -top-4 -right-4 bg-[#4ECDC4] rounded-xl p-4 shadow-xl">
                <div className="text-2xl font-bold text-white">500</div>
                <div className="text-xs text-white/80">Req/hour</div>
              </div>
            </div>
          </div>

          {/* Features grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <div className="text-[#4ECDC4] mb-3">
                <FileCode className="w-8 h-8" />
              </div>
              <h4 className="text-lg font-bold mb-2">Custom JSON</h4>
              <p className="text-white/70 text-sm">
                Bring your own data structure. Paste any JSON and get a working API endpoint instantly.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <div className="text-[#FF6B6B] mb-3">
                <Link className="w-8 h-8" />
              </div>
              <h4 className="text-lg font-bold mb-2">No CORS Issues</h4>
              <p className="text-white/70 text-sm">
                Perfect for frontend development. Call from any domain without CORS restrictions.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <div className="text-purple-400 mb-3">
                <Clock className="w-8 h-8" />
              </div>
              <h4 className="text-lg font-bold mb-2">Track Usage</h4>
              <p className="text-white/70 text-sm">
                Built-in analytics dashboard. Monitor requests, response times, and endpoint performance.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={onOpenDashboard}
                className="group px-8 py-4 bg-white text-[#1A535C] rounded-xl font-bold flex items-center gap-2 hover:bg-gray-100 transition-all hover:scale-105 shadow-xl"
              >
                Create Your Mock API
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => window.location.href = '/docs#mock-apis'}
                className="group px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white rounded-xl font-bold flex items-center gap-2 hover:bg-white/20 transition-all hover:scale-105"
              >
                <BookOpen className="w-5 h-5" />
                View Documentation
              </button>
            </div>
            <p className="text-white/60 text-sm mt-4">Free tier • No credit card required • 10 endpoints included</p>
          </div>
        </div>
      </section>

      {/* Developer Tools Section */}
      <section id="developer-tools" className="py-20 px-6 bg-gradient-to-br from-white via-[#E8F8F6] to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#4ECDC4]/10 rounded-full text-sm text-[#4ECDC4] font-medium mb-6">
              <Wrench className="w-4 h-4" />
              Developer Tools
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Built-in Developer Utilities</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A complete suite of essential developer tools, all in one place. No more switching between tabs and websites.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {/* Diff Viewer */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#4ECDC4] transition-all group flex flex-col">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-lg shadow-md group-hover:shadow-lg transition-all">
                  <GitCompare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Diff Viewer</h3>
                  <p className="text-sm text-gray-600">Compare text and see differences side-by-side</p>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => window.location.href = '/playground'}
                  className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-xs font-medium hover:border-gray-400 hover:bg-gray-50 transition-all"
                >
                  Try Now
                </button>
              </div>
            </div>

            {/* JWT Debugger */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#4ECDC4] transition-all group flex flex-col">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-lg shadow-md group-hover:shadow-lg transition-all">
                  <Key className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">JWT Debugger</h3>
                  <p className="text-sm text-gray-600">Decode and inspect JWT tokens instantly</p>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => window.location.href = '/dev-tools/jwt-debugger'}
                  className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-xs font-medium hover:border-gray-400 hover:bg-gray-50 transition-all"
                >
                  Try Now
                </button>
              </div>
            </div>

            {/* Base64 Tool */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#4ECDC4] transition-all group flex flex-col">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-lg shadow-md group-hover:shadow-lg transition-all">
                  <Lock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Base64 Tool</h3>
                  <p className="text-sm text-gray-600">Encode and decode Base64 strings</p>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => window.location.href = '/dev-tools/base64'}
                  className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-xs font-medium hover:border-gray-400 hover:bg-gray-50 transition-all"
                >
                  Try Now
                </button>
              </div>
            </div>

            {/* JSON Formatter */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#4ECDC4] transition-all group flex flex-col">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-lg shadow-md group-hover:shadow-lg transition-all">
                  <FileJson className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">JSON Formatter</h3>
                  <p className="text-sm text-gray-600">Format and minify JSON data with ease</p>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => window.location.href = '/dev-tools/json-formatter'}
                  className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-xs font-medium hover:border-gray-400 hover:bg-gray-50 transition-all"
                >
                  Try Now
                </button>
              </div>
            </div>

            {/* UUID Generator */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#4ECDC4] transition-all group flex flex-col">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-lg shadow-md group-hover:shadow-lg transition-all">
                  <Hash className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">UUID Generator</h3>
                  <p className="text-sm text-gray-600">Generate unique identifiers on demand</p>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => window.location.href = '/dev-tools/uuid-generator'}
                  className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-xs font-medium hover:border-gray-400 hover:bg-gray-50 transition-all"
                >
                  Try Now
                </button>
              </div>
            </div>

            {/* Hash Generator */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#4ECDC4] transition-all group flex flex-col">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-lg shadow-md group-hover:shadow-lg transition-all">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Hash Generator</h3>
                  <p className="text-sm text-gray-600">Generate SHA-256, SHA-384, SHA-512 hashes</p>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => window.location.href = '/dev-tools/hash-generator'}
                  className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-xs font-medium hover:border-gray-400 hover:bg-gray-50 transition-all"
                >
                  Try Now
                </button>
              </div>
            </div>

            {/* Timestamp Converter */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#4ECDC4] transition-all group flex flex-col">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-lg shadow-md group-hover:shadow-lg transition-all">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Timestamp Converter</h3>
                  <p className="text-sm text-gray-600">Convert between Unix and human-readable formats</p>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => window.location.href = '/dev-tools/timestamp-converter'}
                  className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-xs font-medium hover:border-gray-400 hover:bg-gray-50 transition-all"
                >
                  Try Now
                </button>
              </div>
            </div>

            {/* URL Tool */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#4ECDC4] transition-all group flex flex-col">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-lg shadow-md group-hover:shadow-lg transition-all">
                  <Link className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">URL Encoder/Decoder</h3>
                  <p className="text-sm text-gray-600">Encode and decode URL components</p>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => window.location.href = '/dev-tools/url-encoder'}
                  className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-xs font-medium hover:border-gray-400 hover:bg-gray-50 transition-all"
                >
                  Try Now
                </button>
              </div>
            </div>

            {/* Mock Data Generator */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#4ECDC4] transition-all group flex flex-col">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-lg shadow-md group-hover:shadow-lg transition-all">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Mock Data Generator</h3>
                  <p className="text-sm text-gray-600">Generate realistic test data quickly</p>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => window.location.href = '/dev-tools/mock-data-generator'}
                  className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-xs font-medium hover:border-gray-400 hover:bg-gray-50 transition-all"
                >
                  Try Now
                </button>
              </div>
            </div>

            {/* YAML Converter */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#4ECDC4] transition-all group flex flex-col">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-lg shadow-md group-hover:shadow-lg transition-all">
                  <FileCode className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">YAML ↔ JSON</h3>
                  <p className="text-sm text-gray-600">Convert between YAML and JSON formats</p>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => window.location.href = '/dev-tools/yaml-converter'}
                  className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-xs font-medium hover:border-gray-400 hover:bg-gray-50 transition-all"
                >
                  Try Now
                </button>
              </div>
            </div>

            {/* ENV Converter */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#4ECDC4] transition-all group flex flex-col">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-lg shadow-md group-hover:shadow-lg transition-all">
                  <Settings className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">ENV to YAML</h3>
                  <p className="text-sm text-gray-600">Convert environment files to YAML</p>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => window.location.href = '/dev-tools/env-converter'}
                  className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-xs font-medium hover:border-gray-400 hover:bg-gray-50 transition-all"
                >
                  Try Now
                </button>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <button
              onClick={onOpenDevTools}
              className="group px-8 py-4 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] text-white rounded-xl font-medium flex items-center gap-2 hover:shadow-lg transition-all hover:scale-105 shadow-md mx-auto"
            >
              <Wrench className="w-5 h-5" />
              Open Developer Tools
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-sm text-gray-500 mt-4">All tools work offline • No data is stored or transmitted</p>
          </div>
        </div>
      </section>

      {/* VS Code Extension */}
      <section id="vscode-extension" className="py-20 px-6 bg-gradient-to-br from-[#1A535C] to-[#14424a]">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-sm text-[#4ECDC4] font-medium mb-6">
                <Download className="w-4 h-4" />
                VS Code Extension
              </div>

              <h2 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                Use JsonToonCraft right in your editor
              </h2>

              <p className="text-xl text-white/80 mb-8 leading-relaxed">
                Convert JSON to TypeScript, Zod, OpenAPI, and TOON without leaving VS Code.
                Install our extension and boost your productivity.
              </p>

              <a
                href="https://marketplace.visualstudio.com/items?itemName=krishcdbry.jsontooncraft"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#FF6B6B] text-white rounded-xl font-medium hover:bg-[#ff5252] transition-all hover:scale-105 shadow-xl"
              >
                <Download className="w-5 h-5" />
                Install Extension
              </a>
            </div>

            {/* Right - Features & Instructions */}
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">How to use</h3>

              <div className="space-y-4 mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#FF6B6B] rounded-lg flex items-center justify-center text-white font-bold">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Install the extension</h4>
                    <p className="text-sm text-gray-600">
                      Search for "JsonToonCraft" in VS Code extensions or install from the marketplace
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#4ECDC4] rounded-lg flex items-center justify-center text-white font-bold">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Select your JSON</h4>
                    <p className="text-sm text-gray-600">
                      Highlight the JSON code you want to convert in your editor
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#1A535C] rounded-lg flex items-center justify-center text-white font-bold">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Convert instantly</h4>
                    <p className="text-sm text-gray-600">
                      Right-click and choose your desired output format or use the command palette
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h4 className="font-semibold text-gray-900 mb-3">Features</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-[#4ECDC4]" />
                    <span>Convert to TypeScript interfaces</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-[#4ECDC4]" />
                    <span>Generate Zod schemas</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-[#4ECDC4]" />
                    <span>Create OpenAPI specifications</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-[#4ECDC4]" />
                    <span>Transform to TOON format</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-[#4ECDC4]" />
                    <span>Works completely offline</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* API Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#FFE5D9] via-white to-[#E8F8F6]">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Visual */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-8">
                <div className="mb-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#4ECDC4]/10 rounded-full text-sm text-[#4ECDC4] font-medium">
                    <Code className="w-4 h-4" />
                    REST API
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm">
                    <div className="text-green-400 mb-2">$ curl -X POST https://api.jsontooncraft.com/convert/typescript \</div>
                    <div className="text-gray-400 pl-4">-H &quot;Authorization: Bearer YOUR_KEY&quot; \</div>
                    <div className="text-gray-400 pl-4">-d &apos;&#123;&quot;input&quot;: &quot;...&quot;&#125;&apos;</div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-[#4ECDC4]/10 text-[#4ECDC4] rounded-full text-xs font-medium">TypeScript</span>
                    <span className="px-3 py-1 bg-[#FF6B6B]/10 text-[#FF6B6B] rounded-full text-xs font-medium">Zod</span>
                    <span className="px-3 py-1 bg-[#1A535C]/10 text-[#1A535C] rounded-full text-xs font-medium">OpenAPI</span>
                    <span className="px-3 py-1 bg-purple-500/10 text-purple-600 rounded-full text-xs font-medium">TOON</span>
                  </div>
                </div>
              </div>

              {/* Floating accents */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#FF6B6B]/20 rounded-2xl rotate-12 animate-float"></div>
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-[#4ECDC4]/20 rounded-full animate-float-delayed"></div>
            </div>

            {/* Right - Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#4ECDC4]/10 rounded-full text-sm text-[#4ECDC4] font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                New: REST API
              </div>

              <h2 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight text-gray-900">
                Integrate conversions into your apps
              </h2>

              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Use our REST API to add JSON conversions to your applications, CI/CD pipelines, and automated workflows.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-[#4ECDC4] rounded-full flex items-center justify-center mt-0.5">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Free Tier Available</h4>
                    <p className="text-sm text-gray-600">10,000 requests/month • 500 requests/hour</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-[#4ECDC4] rounded-full flex items-center justify-center mt-0.5">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Rate Limited & Secure</h4>
                    <p className="text-sm text-gray-600">Built-in protection • HTTPS • CORS enabled</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-[#4ECDC4] rounded-full flex items-center justify-center mt-0.5">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">All Formats Supported</h4>
                    <p className="text-sm text-gray-600">TypeScript, Zod, OpenAPI, TOON in one call</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={onOpenAPIDocs}
                  className="group px-8 py-4 bg-[#4ECDC4] text-white rounded-xl font-medium flex items-center gap-2 hover:bg-[#3db8af] transition-all hover:scale-105 shadow-lg shadow-[#4ECDC4]/20"
                >
                  <BookOpen className="w-5 h-5" />
                  View API Docs
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">How it works</h2>
            <p className="text-gray-600">Simple, fast, and powerful</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-[#FF6B6B] rounded-xl flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                1
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Paste JSON</h3>
              <p className="text-gray-600 text-sm">
                Drop your JSON data into the editor. No sign-up, no configuration.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-[#4ECDC4] rounded-xl flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                2
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Auto Convert</h3>
              <p className="text-gray-600 text-sm">
                Instantly see TypeScript, Zod, OpenAPI, and TOON outputs.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-[#1A535C] rounded-xl flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                3
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Copy & Use</h3>
              <p className="text-gray-600 text-sm">
                Copy to clipboard or download files. That's it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Frequently Asked Questions</h2>
            <p className="text-gray-600">Everything you need to know</p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "What is TOON format?",
                a: "TOON is a compact, human-readable data format designed to reduce token usage in LLM applications. It can reduce JSON token count by 30-60%, making it perfect for AI applications, prompt optimization, and reducing API costs."
              },
              {
                q: "Is my data stored anywhere?",
                a: "No! JsonToonCraft is 100% client-side. All conversions happen in your browser. Your data never leaves your computer, and we don't store, track, or transmit anything. No servers, no databases, complete privacy."
              },
              {
                q: "How accurate are the generated schemas?",
                a: "The generated TypeScript interfaces, Zod schemas, and OpenAPI specs are production-ready. They're based on your JSON structure and handle nested objects, arrays, and various data types. However, always review and test before using in production."
              },
              {
                q: "Can I use this for production?",
                a: "Absolutely! JsonToonCraft generates production-quality code. The TypeScript interfaces are type-safe, Zod schemas include runtime validation, and OpenAPI specs are version 3.1 compatible. Just review and integrate into your codebase."
              },
              {
                q: "Do I need to sign up or pay?",
                a: "Nope! JsonToonCraft is completely free to use. No registration, no credit card, no hidden fees. Use it as much as you want, forever. We believe developer tools should be accessible to everyone."
              },
              {
                q: "What formats are supported?",
                a: "Currently supports: JSON ↔ TypeScript interfaces, Zod schemas, OpenAPI 3.1 specifications, and TOON format. You can convert between JSON and TOON bidirectionally, and generate TypeScript/Zod/OpenAPI from either."
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-xl overflow-hidden bg-white hover:shadow-md transition-shadow"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{faq.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-gray-600 leading-relaxed">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#1A535C] to-[#14424a] rounded-3xl p-12 text-center text-white">
            <h2 className="text-4xl font-bold mb-4">Ready to start?</h2>
            <p className="text-xl text-white/80 mb-8">
              Free forever. No credit card. No sign-up.
            </p>
            <button
              onClick={onGetStarted}
              className="px-10 py-4 bg-[#FF6B6B] text-white rounded-xl font-semibold text-lg hover:bg-[#ff5252] transition-all hover:scale-105 shadow-xl"
            >
              Launch JsonToonCraft
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-16 px-6 bg-gradient-to-br from-white to-gray-50 border-t border-gray-200 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF6B6B]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#4ECDC4]/5 rounded-full blur-3xl"></div>

        <div className="relative max-w-6xl mx-auto">
          <div className="flex flex-col items-center gap-8">
            {/* Logo & Brand */}
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 bg-[#FF6B6B]/20 rounded-2xl blur-xl"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-[#FF6B6B] to-[#ff5252] rounded-2xl flex items-center justify-center rotate-3 shadow-xl">
                  <Box className="w-8 h-8 text-white" />
                </div>
              </div>

              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  JsonToonCraft
                </h3>
                <p className="text-gray-600 max-w-md">
                  Transform your data. Build faster. Ship better.
                </p>
              </div>
            </div>

            {/* Developer Info */}
            <div className="flex flex-col items-center gap-2">
              <p className="text-sm text-gray-600">
                Crafted with <span className="text-[#FF6B6B]">❤️</span> by{' '}
                <a
                  href="https://krishcdbry.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-[#FF6B6B] hover:text-[#ff5252] transition-colors"
                >
                  Krish
                </a>
              </p>
              <p className="text-xs text-gray-500 font-medium">
                Free Forever • No Sign-Up Required
              </p>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a
                href="https://discord.gg/VeesFKU8"
                target="_blank"
                rel="noopener noreferrer"
                className="group p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Join Discord Community"
              >
                <svg className="w-5 h-5 text-gray-600 group-hover:text-[#5865F2] transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/>
                </svg>
              </a>

              <a
                href="https://krishcdbry.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Portfolio"
              >
                <svg className="w-5 h-5 text-gray-600 group-hover:text-[#FF6B6B] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </a>

              <a
                href="https://github.com/krishcdbry"
                target="_blank"
                rel="noopener noreferrer"
                className="group p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="GitHub"
              >
                <svg className="w-5 h-5 text-gray-600 group-hover:text-[#FF6B6B] transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                </svg>
              </a>

              <a
                href="https://x.com/krishcdbry"
                target="_blank"
                rel="noopener noreferrer"
                className="group p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="X / Twitter"
              >
                <svg className="w-5 h-5 text-gray-600 group-hover:text-[#4ECDC4] transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>

              <a
                href="https://linkedin.com/in/krishcdbry"
                target="_blank"
                rel="noopener noreferrer"
                className="group p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="LinkedIn"
              >
                <svg className="w-5 h-5 text-gray-600 group-hover:text-[#1A535C] transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path>
                </svg>
              </a>
            </div>

            {/* Copyright */}
            <div className="pt-6 border-t border-gray-200 w-full text-center">
              <p className="text-xs text-gray-500">
                © {new Date().getFullYear()} JsonToonCraft. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Discord Button */}
      <a
        href="https://discord.gg/VeesFKU8"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 group flex items-center gap-2 px-4 py-3 bg-[#5865F2] text-white rounded-full shadow-lg hover:shadow-xl hover:bg-[#4752C4] transition-all hover:scale-105"
        title="Join Discord Community"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/>
        </svg>
        <span className="text-sm font-medium hidden sm:inline">Join Community</span>
      </a>
    </div>
  );
}

export default LandingPage;
